import React, { useState, useEffect, useRef } from 'react';
import { posService } from '../api/posService';
import { clientService } from '../api/clientService';
import './POS.css';

function POS() {
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '' });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchTodaySummary();
    fetchClients();
  }, []);

  const fetchTodaySummary = async () => {
    try {
      const data = await posService.getTodaySummary();
      setSummary(data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientService.getClients({ per_page: 10 });
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleSearch = async (value) => {
    setSearch(value);
    if (value.length >= 2) {
      try {
        const results = await posService.searchProducts(value);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching products:', error);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleBarcodeSearch = async (barcode) => {
    try {
      const product = await posService.searchByBarcode(barcode);
      addToCart(product);
      setSearch('');
      setSearchResults([]);
    } catch (error) {
      alert('Produit non trouvé');
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find((item) => item.product.id === product.id);
    if (existingItem) {
      const newQuantity = existingItem.quantity + 1;

      setCart(
        cart.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    } else {
      setCart([...cart, { product, quantity: 1, unit_price: product.price }]);
    }
    setSearch('');
    setSearchResults([]);
    searchInputRef.current?.focus();
  };

  const updateQuantity = (productId, quantity) => {
    // Prevent negative quantities, cap at 0
    const validQuantity = Math.max(0, quantity);

    if (validQuantity === 0) {
      removeFromCart(productId);
      return;
    }

    setCart(
      cart.map((item) =>
        item.product.id === productId ? { ...item, quantity: validQuantity } : item
      )
    );
  };

  const updatePrice = (productId, price) => {
    // Prevent negative prices, cap at 0
    const validPrice = Math.max(0, parseFloat(price) || 0);
    setCart(
      cart.map((item) =>
        item.product.id === productId ? { ...item, unit_price: validPrice } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.product.id !== productId));
  };

  const calculateTotal = () => {
    return cart.reduce(
      (sum, item) => sum + item.quantity * parseFloat(item.unit_price),
      0
    );
  };

  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      alert('Le panier est vide');
      return;
    }

    setLoading(true);
    try {
      const saleData = {
        client_id: selectedClient?.id || null,
        client_name: selectedClient ? null : newClient.name || null,
        client_email: selectedClient ? null : newClient.email || null,
        client_phone: selectedClient ? null : newClient.phone || null,
        items: cart.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
        payment_method: paymentMethod,
        notes: notes || null,
      };

      await posService.createSale(saleData);
      alert('Vente terminée avec succès !');

      // Reset
      setCart([]);
      setSelectedClient(null);
      setNewClient({ name: '', email: '', phone: '' });
      setNotes('');
      setPaymentMethod('cash');
      fetchTodaySummary();
      searchInputRef.current?.focus();
    } catch (error) {
      alert('Échec de la vente : ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pos-container">
      {/* Header */}
      <div className="pos-header">
        <h1>Point de vente</h1>
        {summary && (
          <div className="pos-summary">
            <div className="summary-item">
              <span>Ventes du jour :</span>
              <strong>{parseFloat(summary.total_sales || 0).toFixed(2)} DH</strong>
            </div>
            <div className="summary-item">
              <span>Commandes :</span>
              <strong>{summary.total_orders || 0}</strong>
            </div>
          </div>
        )}
      </div>

      <div className="pos-main">
        {/* Left Panel - Product Search & Cart */}
        <div className="pos-left">
          {/* Search */}
          <div className="pos-search">
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && search.length > 10) {
                  handleBarcodeSearch(search);
                }
              }}
              placeholder="Rechercher par nom, SKU ou scanner le code-barres..."
              autoFocus
            />
            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((product) => (
                  <div
                    key={product.id}
                    className="search-result-item"
                    onClick={() => addToCart(product)}
                  >
                    <div>
                      <strong>{product.name}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        SKU: {product.sku} | Stock: {product.stock_quantity}
                      </div>
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#3b82f6' }}>
                      {parseFloat(product.price).toFixed(2)} DH
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart */}
          <div className="pos-cart">
            <h3>Panier ({cart.length} articles)</h3>
            {cart.length === 0 ? (
              <div className="empty-cart">Le panier est vide</div>
            ) : (
              <div className="cart-items">
                {cart.map((item) => (
                  <div key={item.product.id} className="cart-item">
                    <div className="cart-item-details">
                      <strong>{item.product.name}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        SKU: {item.product.sku} | Disponible: {item.product.stock_quantity}
                      </div>
                      {item.quantity > item.product.stock_quantity && (
                        <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: 'bold' }}>
                          ⚠️ Attention : Dépasse le stock de {item.quantity - item.product.stock_quantity} unités
                        </div>
                      )}
                      {item.product.stock_quantity <= 0 && (
                        <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: 'bold' }}>
                          ⚠️ Attention : Rupture de stock
                        </div>
                      )}
                    </div>
                    <div className="cart-item-controls">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          updateQuantity(item.product.id, parseInt(e.target.value) || 0)
                        }
                        min="0"
                        style={{
                          width: '60px',
                          borderColor: item.quantity > item.product.stock_quantity ? '#ef4444' : '#d1d5db',
                          backgroundColor: item.quantity > item.product.stock_quantity ? '#fef2f2' : 'white'
                        }}
                      />
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) =>
                          updatePrice(item.product.id, parseFloat(e.target.value) || 0)
                        }
                        step="0.01"
                        min="0"
                        style={{ width: '80px' }}
                      />
                      <strong>
                        {(item.quantity * parseFloat(item.unit_price)).toFixed(2)} DH
                      </strong>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="btn-remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="cart-total">
              <h2>Total : {calculateTotal().toFixed(2)} DH</h2>
            </div>
          </div>
        </div>

        {/* Right Panel - Client & Payment */}
        <div className="pos-right">
          {/* Client Selection */}
          <div className="pos-section">
            <h3>Client (Optionnel)</h3>
            <select
              value={selectedClient?.id || ''}
              onChange={(e) => {
                const client = clients.find((c) => c.id === parseInt(e.target.value));
                setSelectedClient(client || null);
              }}
            >
              <option value="">Client de passage</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>

            {!selectedClient && (
              <div className="new-client-form">
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Nom du client (optionnel)"
                />
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="Email (optionnel)"
                />
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="Téléphone (optionnel)"
                />
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="pos-section">
            <h3>Méthode de paiement</h3>
            <div className="payment-methods">
              {['cash', 'card', 'transfer'].map((method) => (
                <button
                  key={method}
                  className={`payment-btn ${paymentMethod === method ? 'active' : ''}`}
                  onClick={() => setPaymentMethod(method)}
                >
                  {method === 'cash' ? 'Espèces' : method === 'card' ? 'Carte' : 'Virement'}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="pos-section">
            <h3>Notes</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes de commande..."
              rows="3"
            />
          </div>

          {/* Complete Sale Button */}
          <button
            onClick={handleCompleteSale}
            disabled={cart.length === 0 || loading}
            className="btn-complete-sale"
          >
            {loading ? 'Traitement...' : `Finaliser la vente - ${calculateTotal().toFixed(2)} DH`}
          </button>

          <button
            onClick={() => setCart([])}
            disabled={cart.length === 0}
            className="btn-clear-cart"
          >
            Vider le panier
          </button>
        </div>
      </div>
    </div>
  );
}

export default POS;
